import { useEffect, useRef, useState } from "react";
import { api, apiError, downloadFile } from "../../lib/api";
import { toast } from "sonner";
import { Modal } from "../portal/CustomerPortal";
import { DemoBadge } from "../../components/bits";
import { Send, Sparkles, Download, Check, XCircle, FileDown, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks, format, isSameMonth, isSameDay } from "date-fns";

const KPI = [
  ["todays_jobs", "Today's Jobs"], ["jobs_this_week", "Jobs This Week"], ["new_enquiries", "New Enquiries"],
  ["quotes_awaiting", "Quotes Awaiting"], ["confirmed_revenue", "Confirmed Revenue", "£"], ["outstanding_invoices", "Outstanding Invoices", "£"],
  ["cleaner_availability", "Cleaners Available"], ["hours_scheduled", "Hours Scheduled"], ["hours_completed", "Hours Completed"],
  ["recurring_customers", "Recurring Customers"], ["customer_issues", "Customer Issues"], ["staff_alerts", "Staff Alerts"],
  ["compliance_alerts", "Compliance Alerts"], ["open_tasks", "Open Tasks"], ["total_leads", "Business Growth (Leads)"],
];

export function Dashboard() {
  const [stats, setStats] = useState(null);
  const [weekly, setWeekly] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/reports/weekly").then(({ data }) => setWeekly(data)).catch(() => {});
  }, []);
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-3xl tracking-tight">Management HQ</h2>
        {stats?.has_demo && <DemoBadge />}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mt-5">
        {KPI.map(([k, label, prefix]) => (
          <div key={k} className="bg-white border border-[#e8e5de] rounded-sm p-4" data-testid={`kpi-${k}`}>
            <p className="text-[11px] text-[#8a8a8a] uppercase tracking-wide">{label}</p>
            <p className="font-display text-3xl mt-2">{prefix || ""}{stats ? (stats[k] ?? 0).toLocaleString() : "—"}</p>
          </div>
        ))}
      </div>

      {weekly && (
        <div className="bg-[#0a0a0a] text-[#fdfbf7] rounded-sm p-6 mt-6">
          <div className="flex items-center gap-2"><span className="diamond" /><h3 className="font-display text-2xl">CEO Weekly Report</h3></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 text-sm">
            {[["Revenue booked", `£${weekly.revenue_booked}`], ["Hours completed", weekly.jobs_completed],
              ["New enquiries", weekly.new_enquiries], ["Quotes outstanding", weekly.quotes_outstanding],
              ["Unpaid invoices", `£${weekly.unpaid_invoices}`], ["Staffing capacity", weekly.staffing_capacity],
              ["Complaints/issues", weekly.complaints], ["Compliance items", weekly.compliance_items]].map(([l, v]) => (
              <div key={l}><p className="text-[#a3a3a3] text-xs">{l}</p><p className="font-display text-2xl mt-1 text-[#d4af37]">{v}</p></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Quotes ----------------
export function Quotes() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/quotes").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (q) => {
    try { await api.put(`/quotes/${q.id}`, { package: q.package, quoted_price: q.quoted_price ? Number(q.quoted_price) : null, scope: q.scope, exclusions: q.exclusions, validity_days: Number(q.validity_days) || 14, status: q.status }); toast.success("Quote updated"); setEdit(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <h2 className="font-display text-3xl tracking-tight mb-5">Quotations & Enquiries</h2>
      <div className="bg-white border border-[#e8e5de] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#e8e5de]">
            {["Ref", "Customer", "Service", "Status", "Price", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((q) => (
              <tr key={q.id} className="border-b border-[#f0efe9] hover:bg-[#faf9f5]" data-testid={`quote-row-${q.id}`}>
                <td className="px-4 py-3 font-medium">{q.reference} {q.demo && <DemoBadge />}</td>
                <td className="px-4 py-3">{q.customer_name}</td>
                <td className="px-4 py-3">{q.service}</td>
                <td className="px-4 py-3"><span className="text-[#b5952f]">{q.status}</span></td>
                <td className="px-4 py-3">{q.quoted_price != null ? `£${q.quoted_price}` : "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => setEdit(q)} data-testid={`quote-edit-${q.id}`} className="btn-outline-gold px-3 py-1.5 rounded-sm text-xs">Prepare</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-[#8a8a8a]">No quotes yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <QuoteEditor q={edit} onSave={save} onClose={() => setEdit(null)} />}
    </div>
  );
}

function QuoteEditor({ q, onSave, onClose }) {
  const [f, setF] = useState({ ...q });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const cls = "w-full bg-white border border-[#e0ddd4] rounded-sm px-3 py-2.5 text-sm focus:border-[#d4af37]";
  const emailQuote = async () => {
    try { const { data } = await api.post(`/quotes/${q.id}/email`); toast.success(data.note || "Quotation emailed to customer"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal onClose={onClose} title={`Quotation ${q.reference}`} wide>
      <div className="space-y-3 mt-3 text-sm">
        <p className="text-[#555]">{q.customer_name} · {q.customer_email} · {q.postcode}</p>
        <p className="text-[#555]">{q.property_type} · {q.bedrooms || 0} bed / {q.bathrooms || 0} bath · {q.service} · {q.frequency}</p>
        {q.requires_manual && <p className="text-xs text-[#b5952f]">This job requires manual review — no binding price auto-generated.</p>}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs uppercase text-[#555]">Package</label><input className={cls} value={f.package || ""} onChange={(e) => set("package", e.target.value)} data-testid="quote-package" /></div>
          <div><label className="text-xs uppercase text-[#555]">Quoted price (£)</label><input type="number" className={cls} value={f.quoted_price ?? ""} onChange={(e) => set("quoted_price", e.target.value)} data-testid="quote-price" /></div>
        </div>
        <div><label className="text-xs uppercase text-[#555]">Scope</label><textarea rows="2" className={cls} value={f.scope || ""} onChange={(e) => set("scope", e.target.value)} /></div>
        <div><label className="text-xs uppercase text-[#555]">Exclusions</label><input className={cls} value={f.exclusions || ""} onChange={(e) => set("exclusions", e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs uppercase text-[#555]">Validity (days)</label><input type="number" className={cls} value={f.validity_days || 14} onChange={(e) => set("validity_days", e.target.value)} /></div>
          <div><label className="text-xs uppercase text-[#555]">Status</label>
            <select className={cls} value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="quote-status">
              {["Enquiry", "Quote Pending", "Quote Sent", "Awaiting Customer", "Accepted", "Cancelled"].map((s) => <option key={s}>{s}</option>)}
            </select></div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => onSave(f)} data-testid="quote-save" className="btn-gold px-6 py-2.5 rounded-sm text-sm">Save &amp; notify customer</button>
          <button onClick={() => downloadFile(`/documents/quote/${q.id}.pdf`, `${q.reference}.pdf`)} data-testid="quote-pdf" className="btn-outline-gold px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-2"><FileDown size={15} /> PDF</button>
          <button onClick={emailQuote} data-testid="quote-email" className="btn-outline-gold px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-2"><Mail size={15} /> Email</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Bookings ----------------
export function Bookings() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [assign, setAssign] = useState(null);
  const [alloc, setAlloc] = useState(null);
  const [loadingAlloc, setLoadingAlloc] = useState(false);
  const load = () => api.get("/bookings").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); api.get("/res/staff").then(({ data }) => setStaff(data)).catch(() => {}); }, []);
  const openAssign = async (b) => {
    setAssign(b); setAlloc(null); setLoadingAlloc(true);
    try { const { data } = await api.get(`/bookings/${b.id}/allocation`); setAlloc(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoadingAlloc(false); }
  };
  const doAssign = async (staffId) => {
    try { await api.post(`/bookings/${assign.id}/assign`, { staff_id: staffId }); toast.success("Cleaner assigned"); setAssign(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const genNext = async (id) => {
    try { const { data } = await api.post(`/bookings/${id}/generate-next`); toast.success(`Next occurrence ${data.booking.reference} created (${data.booking.date})`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const stopRec = async (id) => {
    try { await api.post(`/bookings/${id}/cancel-recurrence`); toast.success("Recurrence stopped"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const invoice = async (id) => {
    try { const { data } = await api.post(`/bookings/${id}/invoice`); toast.success(`Invoice ${data.invoice_number} generated`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { try { await api.put(`/bookings/${id}`, { status }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const RECURRING = ["Weekly", "Fortnightly", "Monthly", "4-Weekly"];
  return (
    <div>
      <h2 className="font-display text-3xl tracking-tight mb-5">Bookings & Scheduling</h2>
      <div className="bg-white border border-[#e8e5de] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#e8e5de]">
            {["Ref", "Customer", "Service", "Date", "Cleaner", "Profit", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-b border-[#f0efe9] hover:bg-[#faf9f5]" data-testid={`booking-row-${b.id}`}>
                <td className="px-4 py-3 font-medium">{b.reference} {b.demo && <DemoBadge />}
                  {RECURRING.includes(b.frequency) && <span className="ml-1 text-[10px] uppercase tracking-wide text-[#b5952f]" title={`Recurring: ${b.frequency}`}>↻ {b.frequency}</span>}</td>
                <td className="px-4 py-3">{b.customer_name}</td>
                <td className="px-4 py-3">{b.service}</td>
                <td className="px-4 py-3">{b.date || "TBC"}<div className="text-[11px] text-[#9a9a9a]">{b.arrival_window}</div></td>
                <td className="px-4 py-3">{b.assigned_staff?.name || <span className="text-[#b5952f]">Unassigned</span>}</td>
                <td className="px-4 py-3">{b.profit ? <span className={b.profit.below_threshold ? "text-red-500 font-semibold" : "text-green-600"} title={`Rev £${b.profit.revenue} − labour £${b.profit.labour} − consumables £${b.profit.consumables} − fees £${b.profit.fees} − overhead £${b.profit.overhead}`}>£{b.profit.profit}{b.profit.below_threshold ? " ⚠" : ""}</span> : <span className="text-[#ccc]">—</span>}</td>
                <td className="px-4 py-3">
                  <select value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} className="text-xs border border-[#e0ddd4] rounded-sm px-2 py-1" data-testid={`booking-status-${b.id}`}>
                    {["Enquiry", "Quote Pending", "Quote Sent", "Awaiting Customer", "Confirmed", "Assigned", "Cleaner En Route", "In Progress", "Completed", "Quality Check", "Closed", "No Access", "Cancelled", "Cancelled (Late)"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openAssign(b)} data-testid={`assign-${b.id}`} className="btn-outline-gold px-3 py-1.5 rounded-sm text-xs mr-2">Smart Allocate</button>
                  {RECURRING.includes(b.frequency) && b.recurrence_active !== false && (
                    <button onClick={() => stopRec(b.id)} data-testid={`stop-recurrence-${b.id}`} className="btn-outline-gold px-3 py-1.5 rounded-sm text-xs mr-2">Stop ↻</button>
                  )}
                  <button onClick={() => invoice(b.id)} data-testid={`invoice-${b.id}`} className="btn-gold px-3 py-1.5 rounded-sm text-xs">Invoice</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="8" className="px-4 py-10 text-center text-[#8a8a8a]">No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {assign && (
        <Modal onClose={() => setAssign(null)} title={`Smart Allocation · ${assign.reference}`}>
          <p className="text-sm text-[#555] mt-2">{assign.date || "Date TBC"} · {assign.arrival_window || ""} · {assign.postcode || "No postcode"}</p>
          {loadingAlloc && <p className="text-sm text-[#8a8a8a] mt-4">Calculating travel times & availability…</p>}
          {!loadingAlloc && alloc && (
            <div className="mt-4 space-y-2">
              {alloc.candidates.length === 0 && <p className="text-sm text-red-500">No cleaner is available for this slot (all conflict on travel/time). Reschedule or review capacity.</p>}
              {alloc.candidates.map((c, i) => (
                <button key={c.staff_id} data-testid={`assign-staff-${c.staff_id}`} onClick={() => doAssign(c.staff_id)}
                  className={`w-full text-left rounded-sm px-4 py-3 text-sm border ${i === 0 ? "border-[#d4af37] bg-[#fdfaf0]" : "border-[#e0ddd4] hover:border-[#d4af37]"}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{c.name} {i === 0 && <span className="text-[10px] uppercase tracking-wide text-[#b5952f] ml-1">Recommended</span>}</span>
                    <span className="text-[#b5952f] font-medium">{c.travel_minutes}m travel {c.travel_live ? "" : "(est)"}</span>
                  </div>
                  <div className="text-[12px] text-[#777] mt-1 flex gap-3 flex-wrap">
                    <span>Leave by <b>{c.leave_by || "—"}</b></span>
                    <span>Expected pay <b>£{c.expected_pay}</b></span>
                    {c.is_driver && <span className="text-[#b5952f]">Driver</span>}
                    {c.preferred && <span className="text-[#b5952f]">Preferred</span>}
                    {c.area_match && <span>Area match</span>}
                  </div>
                </button>
              ))}
              {alloc.unavailable?.length > 0 && (
                <div className="pt-2 mt-2 border-t border-[#f0e6cf]">
                  <p className="text-xs uppercase tracking-wide text-[#8a8a8a] mb-1">Unavailable</p>
                  {alloc.unavailable.map((u) => <p key={u.staff_id} className="text-[12px] text-[#999]">{u.name} — {u.reason}</p>)}
                </div>
              )}
            </div>
          )}
          {!loadingAlloc && !alloc && (
            <div className="space-y-2 mt-4">
              {staff.filter((s) => s.active).map((s) => (
                <button key={s.id} data-testid={`assign-staff-${s.id}`} onClick={() => doAssign(s.id)} className="w-full text-left border border-[#e0ddd4] hover:border-[#d4af37] rounded-sm px-4 py-3 text-sm">
                  <span className="font-medium">{s.name}</span> <span className="text-[#8a8a8a]">· {(s.service_areas || []).join(", ")}</span>
                </button>
              ))}
            </div>
          )}
          {assign.recurrence_active !== false && ["Weekly", "Fortnightly", "Monthly", "4-Weekly"].includes(assign.frequency) && (
            <button onClick={() => { genNext(assign.id); setAssign(null); }} data-testid={`generate-next-${assign.id}`} className="btn-outline-gold w-full mt-4 px-4 py-2 rounded-sm text-xs">Generate next {assign.frequency} occurrence</button>
          )}
        </Modal>
      )}
    </div>
  );
}

// ---------------- Invoices ----------------
export function Invoices() {
  const [rows, setRows] = useState([]);
  const [pay, setPay] = useState(null);
  const load = () => api.get("/invoices").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const record = async (amount, method) => {
    try { await api.post(`/invoices/${pay.id}/payment`, { amount: Number(amount), method }); toast.success("Payment recorded"); setPay(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const emailInv = async (id) => {
    try { const { data } = await api.post(`/invoices/${id}/email`); toast.success(data.note || "Invoice emailed to customer"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <h2 className="font-display text-3xl tracking-tight mb-5">Invoices & Payments</h2>
      <p className="text-xs text-[#8a8a8a] mb-4">Payments are recorded manually — no live payment gateway is connected. Stripe architecture is ready in Settings → Integrations.</p>
      <div className="bg-white border border-[#e8e5de] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#e8e5de]">
            {["Invoice", "Customer", "Total", "Balance", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} className="border-b border-[#f0efe9] hover:bg-[#faf9f5]" data-testid={`invoice-row-${i.id}`}>
                <td className="px-4 py-3 font-medium">{i.invoice_number} {i.demo && <DemoBadge />}</td>
                <td className="px-4 py-3">{i.customer_name}</td>
                <td className="px-4 py-3">£{i.total}</td>
                <td className="px-4 py-3">£{i.balance}</td>
                <td className="px-4 py-3"><span className={i.status === "Paid" ? "text-green-600" : "text-[#b5952f]"}>{i.status}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => downloadFile(`/documents/invoice/${i.id}.pdf`, `${i.invoice_number}.pdf`)} data-testid={`inv-pdf-${i.id}`} title="Download PDF" className="text-[#8a8a8a] hover:text-[#d4af37] mr-3"><FileDown size={16} /></button>
                  <button onClick={() => emailInv(i.id)} data-testid={`inv-email-${i.id}`} title="Email to customer" className="text-[#8a8a8a] hover:text-[#d4af37] mr-3"><Mail size={16} /></button>
                  {i.status !== "Paid" && <button onClick={() => setPay(i)} data-testid={`pay-${i.id}`} className="btn-outline-gold px-3 py-1.5 rounded-sm text-xs">Record payment</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-[#8a8a8a]">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {pay && <PayModal inv={pay} onClose={() => setPay(null)} onRecord={record} />}
    </div>
  );
}

function PayModal({ inv, onClose, onRecord }) {
  const [amount, setAmount] = useState(inv.balance);
  const [method, setMethod] = useState("card");
  return (
    <Modal onClose={onClose} title={`Record payment · ${inv.invoice_number}`}>
      <p className="text-sm text-[#555] mt-2">Balance: £{inv.balance}</p>
      <label className="text-xs uppercase text-[#555] mt-3 block">Amount (£)</label>
      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2.5 text-sm" />
      <label className="text-xs uppercase text-[#555] mt-3 block">Method</label>
      <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2.5 text-sm"><option value="card">Card</option><option value="bank">Bank transfer</option><option value="cash">Cash</option><option value="link">Payment link</option></select>
      <button onClick={() => onRecord(amount, method)} data-testid="pay-record" className="btn-gold px-6 py-2.5 rounded-sm text-sm mt-4">Record</button>
    </Modal>
  );
}

// ---------------- Discounts ----------------
export function Discounts({ isManagement }) {
  const [rows, setRows] = useState([]);
  const [req, setReq] = useState(false);
  const load = () => api.get("/discounts").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try { await api.post(`/discounts/${id}/decide`, { decision }); toast.success(`Discount ${decision.toLowerCase()}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submit = async (f) => {
    try { await api.post("/discounts/request", { customer: f.customer, booking_ref: f.booking_ref, original_price: Number(f.original_price), proposed_discount: Number(f.proposed_discount), reason: f.reason }); toast.success("Discount request submitted for approval"); setReq(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div><h2 className="font-display text-3xl tracking-tight">Discount Approvals</h2>
          <p className="text-xs text-[#8a8a8a] mt-1">No discount may be applied without Founder/CEO or management approval.</p></div>
        <button onClick={() => setReq(true)} data-testid="discount-request-btn" className="btn-gold px-4 py-2.5 rounded-sm text-sm">Request discount</button>
      </div>
      <div className="space-y-3">
        {rows.map((d) => (
          <div key={d.id} className="bg-white border border-[#e8e5de] rounded-sm p-5 flex justify-between items-center flex-wrap gap-3" data-testid={`discount-${d.id}`}>
            <div>
              <p className="font-medium">{d.customer} · £{d.proposed_discount} off</p>
              <p className="text-sm text-[#555]">{d.reason}</p>
              <p className="text-xs text-[#8a8a8a] mt-1">Requested by {d.requested_by} · {d.booking_ref || "no booking"}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${d.status === "Approved" ? "text-green-600" : d.status === "Rejected" ? "text-red-500" : "text-[#b5952f]"}`}>{d.status}</span>
              {d.status === "Pending" && isManagement && (
                <>
                  <button onClick={() => decide(d.id, "Approved")} data-testid={`approve-${d.id}`} className="btn-gold px-3 py-1.5 rounded-sm text-xs flex items-center gap-1"><Check size={14} /> Approve</button>
                  <button onClick={() => decide(d.id, "Rejected")} data-testid={`reject-${d.id}`} className="btn-outline-gold px-3 py-1.5 rounded-sm text-xs flex items-center gap-1"><XCircle size={14} /> Reject</button>
                </>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="bg-white border border-[#e8e5de] rounded-sm p-10 text-center text-[#8a8a8a]">No discount requests.</div>}
      </div>
      {req && <DiscountForm onClose={() => setReq(false)} onSubmit={submit} />}
    </div>
  );
}

function DiscountForm({ onClose, onSubmit }) {
  const [f, setF] = useState({ customer: "", booking_ref: "", original_price: "", proposed_discount: "", reason: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const cls = "w-full border border-[#e0ddd4] rounded-sm px-3 py-2.5 text-sm";
  return (
    <Modal onClose={onClose} title="Request discount approval">
      <div className="space-y-3 mt-3">
        <input placeholder="Customer" className={cls} value={f.customer} onChange={(e) => set("customer", e.target.value)} data-testid="disc-customer" />
        <input placeholder="Booking reference (optional)" className={cls} value={f.booking_ref} onChange={(e) => set("booking_ref", e.target.value)} />
        <input type="number" placeholder="Original price (£)" className={cls} value={f.original_price} onChange={(e) => set("original_price", e.target.value)} data-testid="disc-original" />
        <input type="number" placeholder="Proposed discount (£)" className={cls} value={f.proposed_discount} onChange={(e) => set("proposed_discount", e.target.value)} data-testid="disc-amount" />
        <textarea rows="2" placeholder="Reason" className={cls} value={f.reason} onChange={(e) => set("reason", e.target.value)} data-testid="disc-reason" />
        <button onClick={() => onSubmit(f)} data-testid="disc-submit" className="btn-gold px-6 py-2.5 rounded-sm text-sm">Submit for approval</button>
      </div>
    </Modal>
  );
}

// ---------------- Diamond AI ----------------
export function DiamondAI() {
  const sid = useRef("diamond-" + Math.random().toString(36).slice(2)).current;
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "I'm Diamond AI, your private operations assistant. Ask me about scheduling, enquiries, quotes, staff capacity or growth. I propose actions — you approve them." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async () => {
    const text = input.trim(); if (!text || busy) return;
    setInput(""); setMsgs((m) => [...m, { role: "user", content: text }]); setBusy(true);
    try { const { data } = await api.post("/ai/diamond", { session_id: sid, message: text }); setMsgs((m) => [...m, { role: "assistant", content: data.reply }]); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-4"><Sparkles className="text-[#d4af37]" /><h2 className="font-display text-3xl tracking-tight">Diamond AI</h2></div>
      <div className="bg-[#0a0a0a] rounded-sm border border-[#333] flex flex-col h-[65vh]">
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-lg text-sm leading-relaxed ${m.role === "user" ? "bg-[#d4af37] text-[#0a0a0a]" : "bg-[#1c1c1c] text-[#e8e5de] border border-[#333]"}`}>{m.content}</div>
            </div>
          ))}
          {busy && <p className="text-[#a3a3a3] text-xs">Diamond AI is thinking…</p>}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-[#333] flex gap-2">
          <input data-testid="diamond-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Diamond AI…" className="flex-1 bg-[#1c1c1c] border border-[#333] text-[#fdfbf7] text-sm px-4 py-2.5 rounded-md placeholder:text-[#666]" />
          <button data-testid="diamond-send" onClick={send} className="btn-gold px-4 rounded-md"><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Reports ----------------
export function Reports({ isManagement }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports/summary").then(({ data }) => setR(data)).catch(() => {}); }, []);
  const exportCsv = async (kind) => {
    try {
      const res = await api.get(`/reports/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${kind}.csv`; a.click();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!r) return <p className="text-[#8a8a8a]">Loading…</p>;
  return (
    <div>
      <h2 className="font-display text-3xl tracking-tight mb-5">Reporting</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Leads", r.leads], ["Quotes", r.quotes], ["Quote conversion", `${r.quote_conversion}%`], ["Bookings", r.bookings],
          ["Revenue", `£${r.revenue}`], ["Outstanding", `£${r.outstanding}`], ["Cancellations", r.cancellations], ["Complaints", r.complaints],
          ["Recurring customers", r.recurring_customers], ["Hours completed", r.hours_completed]].map(([l, v]) => (
          <div key={l} className="bg-white border border-[#e8e5de] rounded-sm p-4"><p className="text-[11px] text-[#8a8a8a] uppercase">{l}</p><p className="font-display text-2xl mt-1">{v}</p></div>
        ))}
      </div>
      <h3 className="font-display text-xl mt-8 mb-3">Service popularity</h3>
      <div className="bg-white border border-[#e8e5de] rounded-sm p-4">
        {r.service_popularity.length === 0 ? <p className="text-[#8a8a8a] text-sm">No bookings yet.</p> :
          r.service_popularity.map(([name, count]) => (
            <div key={name} className="flex items-center gap-3 py-1.5"><span className="w-40 text-sm">{name}</span><div className="flex-1 bg-[#f0efe9] h-2 rounded-full"><div className="bg-[#d4af37] h-2 rounded-full" style={{ width: `${Math.min(count * 20, 100)}%` }} /></div><span className="text-sm">{count}</span></div>
          ))}
      </div>
      {isManagement && (
        <div className="mt-6">
          <p className="text-sm text-[#555] mb-2">Export CSV</p>
          <div className="flex flex-wrap gap-2">
            {["leads", "quotes", "bookings", "invoices", "staff"].map((k) => (
              <button key={k} onClick={() => exportCsv(k)} data-testid={`export-${k}`} className="btn-outline-gold px-4 py-2 rounded-sm text-sm flex items-center gap-2"><Download size={14} /> {k}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Calendar ----------------
export function Calendar() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [buffer, setBuffer] = useState(30);
  useEffect(() => {
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/res/staff").then(({ data }) => setStaff(data.filter((s) => s.active))).catch(() => {});
    api.get("/settings/booking_rules").then(({ data }) => setBuffer(data.value?.travel_buffer_minutes || 30)).catch(() => {});
  }, []);
  const onDay = (d) => bookings.filter((b) => b.date === format(d, "yyyy-MM-dd") && b.status !== "Cancelled");
  const win = (b) => b.arrival_window || "TBC";
  const startTime = (b) => (b.arrival_window || "99:99").split("–")[0].trim();

  const move = (dir) => setCursor((c) => view === "month" ? addMonths(c, dir) : view === "week" ? addWeeks(c, dir) : addDays(c, dir));

  const chip = (b) => (
    <div key={b.id} data-testid={`cal-booking-${b.id}`} className="bg-[#0a0a0a] text-[#fdfbf7] rounded-sm px-2 py-1 text-[11px] mb-1 border-l-2 border-[#d4af37]">
      <span className="text-[#d4af37]">{win(b)}</span> · {b.service}
      <div className="text-[#a3a3a3]">{b.assigned_staff?.name || "Unassigned"}</div>
    </div>
  );

  const header = (
    <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
      <h2 className="font-display text-3xl tracking-tight">Scheduling Calendar</h2>
      <div className="flex items-center gap-2">
        <div className="flex rounded-sm overflow-hidden border border-[#e0ddd4]">
          {["day", "week", "month"].map((v) => (
            <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)} className={`px-3 py-1.5 text-sm capitalize ${view === v ? "bg-[#d4af37] text-[#0a0a0a] font-semibold" : "bg-white text-[#555]"}`}>{v}</button>
          ))}
        </div>
        <button onClick={() => move(-1)} data-testid="cal-prev" className="btn-outline-gold p-2 rounded-sm"><ChevronLeft size={16} /></button>
        <button onClick={() => setCursor(new Date())} data-testid="cal-today" className="btn-outline-gold px-3 py-1.5 rounded-sm text-sm">Today</button>
        <button onClick={() => move(1)} data-testid="cal-next" className="btn-outline-gold p-2 rounded-sm"><ChevronRight size={16} /></button>
      </div>
    </div>
  );

  // MONTH
  const monthGrid = () => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const days = []; let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return (
      <div>
        <p className="font-display text-2xl mb-3">{format(cursor, "MMMM yyyy")}</p>
        <div className="grid grid-cols-7 gap-px bg-[#e0ddd4] border border-[#e0ddd4] rounded-sm overflow-hidden">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="bg-[#0a0a0a] text-[#d4af37] text-xs py-2 text-center">{d}</div>)}
          {days.map((day) => {
            const jobs = onDay(day).sort((a, b) => startTime(a).localeCompare(startTime(b)));
            return (
              <div key={day.toISOString()} className={`bg-white min-h-[96px] p-1.5 ${isSameMonth(day, cursor) ? "" : "opacity-40"} ${isSameDay(day, new Date()) ? "ring-1 ring-[#d4af37] ring-inset" : ""}`}>
                <p className="text-xs text-[#8a8a8a] mb-1">{format(day, "d")}</p>
                {jobs.slice(0, 3).map(chip)}
                {jobs.length > 3 && <p className="text-[10px] text-[#b5952f]">+{jobs.length - 3} more</p>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // WEEK
  const weekGrid = () => {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return (
      <div>
        <p className="font-display text-2xl mb-3">Week of {format(start, "d MMM yyyy")}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {days.map((day) => {
            const jobs = onDay(day).sort((a, b) => startTime(a).localeCompare(startTime(b)));
            return (
              <div key={day.toISOString()} className={`bg-white border rounded-sm p-2 min-h-[120px] ${isSameDay(day, new Date()) ? "border-[#d4af37]" : "border-[#e0ddd4]"}`}>
                <p className="text-xs font-semibold mb-2">{format(day, "EEE d")}</p>
                {jobs.length === 0 ? <p className="text-[10px] text-[#ccc]">—</p> : jobs.map(chip)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // DAY — cleaner columns + travel gaps
  const dayGrid = () => {
    const jobs = onDay(cursor);
    return (
      <div>
        <p className="font-display text-2xl mb-3">{format(cursor, "EEEE d MMMM yyyy")}</p>
        <p className="text-xs text-[#8a8a8a] mb-4">Travel buffer between jobs: {buffer} min (from Booking Rules).</p>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {staff.length === 0 && <p className="text-[#8a8a8a] text-sm">No active staff.</p>}
          {staff.map((s) => {
            const list = jobs.filter((b) => b.assigned_staff?.id === s.id).sort((a, b) => startTime(a).localeCompare(startTime(b)));
            return (
              <div key={s.id} className="min-w-[200px] flex-1 bg-white border border-[#e0ddd4] rounded-sm p-3" data-testid={`cal-cleaner-${s.id}`}>
                <p className="text-sm font-semibold border-b border-[#f0efe9] pb-2 mb-2">{s.name}</p>
                {list.length === 0 ? <p className="text-[11px] text-[#ccc]">No jobs</p> : list.map((b, idx) => (
                  <div key={b.id}>
                    {idx > 0 && <div className="text-[10px] text-[#b5952f] my-1 flex items-center gap-1">↕ +{buffer} min travel</div>}
                    {chip(b)}
                  </div>
                ))}
              </div>
            );
          })}
          {(() => {
            const unassigned = jobs.filter((b) => !b.assigned_staff);
            return unassigned.length > 0 ? (
              <div className="min-w-[200px] flex-1 bg-[#fff8ea] border border-[#d4af37]/40 rounded-sm p-3">
                <p className="text-sm font-semibold border-b border-[#f0e6cf] pb-2 mb-2 text-[#b5952f]">Unassigned</p>
                {unassigned.sort((a, b) => startTime(a).localeCompare(startTime(b))).map(chip)}
              </div>
            ) : null;
          })()}
        </div>
      </div>
    );
  };

  return <div>{header}{view === "month" ? monthGrid() : view === "week" ? weekGrid() : dayGrid()}</div>;
}

// ---------------- Compliance (insurance-aware) ----------------
export function InsurancePanel() {
  const [ins, setIns] = useState(null);
  useEffect(() => { api.get("/settings/insurance").then(({ data }) => setIns(data.value)).catch(() => {}); }, []);
  if (!ins) return null;
  const active = ins.cover_start && new Date().toISOString().slice(0, 10) >= ins.cover_start;
  return (
    <div className="bg-[#0a0a0a] text-[#fdfbf7] rounded-sm p-6 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><span className="diamond" /><h3 className="font-display text-2xl">Insurance — {ins.provider}</h3></div>
        <span data-testid="insurance-status" className={`text-sm font-semibold px-3 py-1 rounded-sm ${active ? "bg-green-600/20 text-green-400 border border-green-600/40" : "bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/40"}`}>
          {active ? "ACTIVE" : ins.status_before}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4 text-sm">
        {ins.public_claims?.map((c) => (
          <p key={c} className="flex items-center gap-2 text-[#cfcabf]"><Check size={15} className="text-[#d4af37]" /> {c}</p>
        ))}
      </div>
      <p className="text-xs text-[#a3a3a3] mt-4">Public claims display on the website only from the cover start date ({ins.cover_start}). Statement of Facts activity: {ins.activity_recorded}.</p>
      <p className="text-xs text-red-400 mt-2 font-medium">{ins.exterior_rule}</p>
      <p className="text-xs text-[#d4af37] mt-1">Commercial work is manual review only until the insurer confirms commercial scope.</p>
    </div>
  );
}

// ---------------- Audit ----------------
export function Audit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/audit").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <div>
      <h2 className="font-display text-3xl tracking-tight mb-5">Audit Log</h2>
      <div className="bg-white border border-[#e8e5de] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#e8e5de]">{["When", "User", "Role", "Action", "Entity"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{rows.map((a) => (
            <tr key={a.id} className="border-b border-[#f0efe9]"><td className="px-4 py-2.5 text-xs">{new Date(a.created_at).toLocaleString("en-GB")}</td><td className="px-4 py-2.5">{a.user_email}</td><td className="px-4 py-2.5">{a.user_role}</td><td className="px-4 py-2.5">{a.action}</td><td className="px-4 py-2.5">{a.entity} {a.entity_id?.slice(0, 6)}</td></tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-[#8a8a8a]">No audit entries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------------- Live Operations ----------------
export function LiveOps() {
  const [data, setData] = useState(null);
  const load = () => api.get("/operations/live").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  if (!data) return <p className="text-[#8a8a8a]">Loading live operations…</p>;
  const A = data.alerts;
  const ALERTS = [
    ["Awaiting allocation", A.awaiting_allocation], ["No access", A.no_access], ["Failed payments", A.failed_payments],
    ["Low-profit jobs", A.low_profit_jobs], ["Compliance alerts", A.compliance_alerts],
    ["Open complaints", A.unresolved_complaints], ["Open incidents", A.open_incidents],
  ];
  const BUCKETS = [
    ["awaiting_allocation", "Awaiting Allocation"], ["assigned_today", "Assigned Today"], ["en_route", "Travelling"],
    ["arrived", "Arrived"], ["in_progress", "In Progress"], ["no_access", "No Access"],
    ["completed_today", "Completed Today"], ["low_profit", "Low / Negative Profit"],
  ];
  return (
    <div data-testid="live-ops">
      <h2 className="font-display text-3xl tracking-tight mb-1">Live Operations</h2>
      <p className="text-[#8a8a8a] text-sm mb-5">Auto-refreshes every 20s · {new Date(data.generated_at).toLocaleTimeString()}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {ALERTS.map(([label, n]) => (
          <div key={label} className={`rounded-sm p-3 border text-center ${n > 0 ? "border-red-300 bg-red-50" : "border-[#e8e5de] bg-white"}`} data-testid={`alert-${label.replace(/\s/g, "-").toLowerCase()}`}>
            <p className={`font-display text-2xl ${n > 0 ? "text-red-500" : "text-[#0a0a0a]"}`}>{n}</p>
            <p className="text-[10px] uppercase tracking-wide text-[#8a8a8a] mt-1">{label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {BUCKETS.map(([key, label]) => (
          <div key={key} className="bg-white border border-[#e8e5de] rounded-sm p-4" data-testid={`bucket-${key}`}>
            <p className="text-[11px] tracking-luxe uppercase text-[#b5952f] mb-2">{label} · {data.buckets[key].length}</p>
            {data.buckets[key].length === 0 ? <p className="text-[#bbb] text-sm">—</p> : (
              <div className="space-y-1.5">
                {data.buckets[key].map((b) => (
                  <div key={b.id} className="flex justify-between text-sm border-b border-[#f3f1ea] pb-1.5">
                    <span><b>{b.reference}</b> · {b.customer_name} <span className="text-[#999]">{b.postcode}</span></span>
                    <span className="text-[#777]">{b.assigned_staff || "—"} {b.below_threshold && <span className="text-red-500">⚠£{b.profit}</span>}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Payroll ----------------
export function Payroll({ isManagement }) {
  const [runs, setRuns] = useState([]);
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const today = new Date();
  const [start, setStart] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const load = () => api.get("/payroll").then(({ data }) => setRuns(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    try { const { data } = await api.post("/payroll/run", { period_start: start, period_end: end }); toast.success(`Payroll run: ${data.run.staff_count} staff · £${data.run.total_gross}`); load(); openRun(data.run); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openRun = async (r) => { setSel(r); const { data } = await api.get(`/payroll/${r.id}/items`); setItems(data); };
  const markPaid = async () => { try { await api.post(`/payroll/${sel.id}/mark-paid`); toast.success("Run marked paid"); openRun(sel); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const cls = "bg-white border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm";
  return (
    <div data-testid="payroll-view">
      <h2 className="font-display text-3xl tracking-tight mb-5">Payroll</h2>
      <div className="bg-white border border-[#e8e5de] rounded-sm p-5 mb-6 flex flex-wrap items-end gap-3">
        <div><label className="text-xs uppercase text-[#555] block mb-1">Period start</label><input type="date" className={cls} value={start} onChange={(e) => setStart(e.target.value)} data-testid="payroll-start" /></div>
        <div><label className="text-xs uppercase text-[#555] block mb-1">Period end</label><input type="date" className={cls} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="payroll-end" /></div>
        {isManagement && <button onClick={run} data-testid="payroll-run" className="btn-gold px-6 py-2 rounded-sm text-sm">Run payroll</button>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-[#e8e5de] rounded-sm overflow-hidden">
          <p className="px-4 py-3 text-[11px] uppercase tracking-luxe text-[#b5952f] border-b border-[#eee]">Payroll runs</p>
          {runs.length === 0 ? <p className="p-6 text-[#8a8a8a] text-sm">No payroll runs yet.</p> : runs.map((r) => (
            <button key={r.id} onClick={() => openRun(r)} data-testid={`payroll-run-${r.id}`} className={`w-full text-left px-4 py-3 border-b border-[#f3f1ea] text-sm hover:bg-[#faf9f5] ${sel?.id === r.id ? "bg-[#fdfaf0]" : ""}`}>
              <div className="flex justify-between"><span>{r.period_start} → {r.period_end}</span><span className={r.status === "Paid" ? "text-green-600" : "text-[#b5952f]"}>{r.status}</span></div>
              <p className="text-xs text-[#8a8a8a]">{r.staff_count} staff · {r.total_hours}h · £{r.total_gross}</p>
            </button>
          ))}
        </div>
        {sel && items && (
          <div className="bg-white border border-[#e8e5de] rounded-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-[#eee] flex justify-between items-center">
              <p className="text-[11px] uppercase tracking-luxe text-[#b5952f]">Items · {sel.period_start}</p>
              {isManagement && sel.status !== "Paid" && <button onClick={markPaid} data-testid="payroll-mark-paid" className="btn-outline-gold px-3 py-1 rounded-sm text-xs">Mark all paid</button>}
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#eee]">{["Cleaner", "Hours", "Jobs", "Gross", "Status"].map((h) => <th key={h} className="px-4 py-2">{h}</th>)}</tr></thead>
              <tbody>{items.map((i) => (
                <tr key={i.id} className="border-b border-[#f3f1ea]"><td className="px-4 py-2">{i.staff_name}</td><td className="px-4 py-2">{i.hours}</td><td className="px-4 py-2">{i.jobs}</td><td className="px-4 py-2">£{i.net}</td><td className="px-4 py-2"><span className={i.status === "Paid" ? "text-green-600" : "text-[#b5952f]"}>{i.status}</span></td></tr>
              ))}{items.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-[#8a8a8a]">No timesheets in this period.</td></tr>}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Family Discount (Owner only) ----------------
export function FamilyDiscount() {
  const [rows, setRows] = useState([]);
  const [usage, setUsage] = useState(null);
  const load = () => api.get("/family").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    const full_name = window.prompt("Full name:"); if (!full_name) return;
    const email = window.prompt("Email (must match their customer account):"); if (!email) return;
    const mobile = window.prompt("Mobile (optional):") || "";
    const relationship = window.prompt("Relationship / notes (optional):") || "";
    const address = window.prompt("Property / address (optional):") || "";
    try { await api.post("/family", { full_name, email, mobile, relationship, address }); toast.success("Family member added"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const act = async (id, action) => { try { await api.post(`/family/${id}/${action}`); toast.success(`Member ${action}d`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const remove = async (id) => { if (!window.confirm("Remove family-discount eligibility? Their customer account & history are preserved.")) return; try { const { data } = await api.delete(`/family/${id}`); toast.success(data.message); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const viewUsage = async (id) => { try { const { data } = await api.get(`/family/${id}/usage`); setUsage(data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div data-testid="family-discount">
      <div className="flex justify-between items-center mb-5">
        <div><h2 className="font-display text-3xl tracking-tight">Family Discount</h2>
          <p className="text-[#8a8a8a] text-sm mt-1">Private Owner list — approved members get a 3-hour Essential clean for £50. Never shown publicly.</p></div>
        <button onClick={add} data-testid="family-add" className="btn-gold px-5 py-2.5 rounded-sm text-sm">Add Person</button>
      </div>
      <div className="bg-white border border-[#e8e5de] rounded-sm overflow-x-auto">
        <table className="w-full text-sm"><thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#e8e5de]">
          {["Name", "Email", "Mobile", "Relationship", "Cleans used", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
        </tr></thead><tbody>
          {rows.map((m) => (
            <tr key={m.id} className="border-b border-[#f0efe9]" data-testid={`family-row-${m.id}`}>
              <td className="px-4 py-3 font-medium">{m.full_name}</td><td className="px-4 py-3">{m.email}</td>
              <td className="px-4 py-3">{m.mobile || "—"}</td><td className="px-4 py-3">{m.relationship || "—"}</td>
              <td className="px-4 py-3">{m.cleans_used || 0}</td>
              <td className="px-4 py-3"><span className={m.active ? "text-green-600" : "text-[#b5952f]"}>{m.active ? "Active" : "Suspended"}</span></td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button onClick={() => viewUsage(m.id)} className="text-xs text-[#b5952f] mr-3">Usage</button>
                {m.active ? <button data-testid={`family-suspend-${m.id}`} onClick={() => act(m.id, "suspend")} className="text-xs text-[#b5952f] mr-3">Suspend</button>
                          : <button data-testid={`family-activate-${m.id}`} onClick={() => act(m.id, "activate")} className="text-xs text-green-600 mr-3">Activate</button>}
                <button data-testid={`family-remove-${m.id}`} onClick={() => remove(m.id)} className="text-xs text-red-500">Remove</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-[#8a8a8a]">No family members yet.</td></tr>}
        </tbody></table>
      </div>
      {usage && <Modal onClose={() => setUsage(null)} title={`Usage · ${usage.member.full_name}`} wide>
        <p className="text-sm text-[#555] mt-2">{usage.total} family-rate booking(s)</p>
        <div className="mt-3 space-y-2">{usage.bookings.map((b) => <div key={b.id} className="flex justify-between text-sm border-b border-[#f0efe9] py-2"><span>{b.reference} · {b.date}</span><span>£{b.price} · {b.status}</span></div>)}
        {usage.bookings.length === 0 && <p className="text-[#8a8a8a] text-sm">No bookings yet.</p>}</div>
      </Modal>}
    </div>
  );
}

// ---------------- Owner Elite Signature (Owner only) ----------------
export function OwnerBenefit() {
  const [d, setD] = useState(null);
  const [staff, setStaff] = useState([]);
  const [report, setReport] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/owner/benefit").then(({ data }) => setD(data)).catch(() => {});
  const loadReport = () => api.get("/owner/cost-report").then(({ data }) => setReport(data)).catch(() => {});
  useEffect(() => { load(); loadReport(); api.get("/res/staff").then(({ data }) => setStaff(data)).catch(() => {}); }, []);
  const gen = async () => { try { const { data } = await api.post("/owner/benefit/generate"); toast.success(`${data.created} visit(s) generated`); load(); loadReport(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const pause = async (p) => { try { await api.post(`/owner/benefit/${p ? "pause" : "resume"}`); toast.success(p ? "Paused" : "Resumed"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const cancelRec = async () => { if (!window.confirm("Stop the recurring Owner arrangement? Existing visits remain.")) return; try { const { data } = await api.post("/owner/benefit/cancel-recurring"); toast.success(data.message); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const cancelVisit = async (id) => { try { await api.post(`/owner/benefit/cancel-visit/${id}`); toast.success("Visit cancelled"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const addPriority = async () => { const text = window.prompt("Priority (e.g. 'Deep clean oven Thursday'):"); if (!text) return; const level = (window.prompt("Level: NORMAL / HIGH / URGENT", "HIGH") || "HIGH").toUpperCase(); try { await api.post("/owner/priorities", { text, level }); toast.success("Priority added"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const delPriority = async (id) => { try { await api.delete(`/owner/priorities/${id}`); load(); } catch {} };
  const saveSettings = async () => { try { const { data } = await api.put("/owner/benefit", edit); toast.success("Owner settings saved"); setEdit(null); setD((p) => ({ ...p, config: data.config })); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  if (!d) return <p className="text-[#8a8a8a]">Loading…</p>;
  const cfg = d.config || {};
  const cleaner = staff.find((s) => s.id === cfg.preferred_cleaner_id);
  return (
    <div data-testid="owner-benefit">
      <div className="flex justify-between items-start mb-5">
        <div><h2 className="font-display text-3xl tracking-tight">Owner — Elite Signature</h2>
          <p className="text-[#8a8a8a] text-sm mt-1">Private benefit — no retail charge. You pay only the actual cleaner labour cost. Never shown publicly.</p></div>
        <button onClick={() => setEdit({ address: cfg.address || "", postcode: cfg.postcode || "", access_info: cfg.access_info || "", preferred_cleaner_id: cfg.preferred_cleaner_id || "" })} data-testid="owner-edit-settings" className="btn-outline-gold px-4 py-2 rounded-sm text-sm">Edit settings</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#0a0a0a] text-[#fdfbf7] rounded-sm p-5" data-testid="owner-cost">
          <p className="text-[11px] tracking-luxe uppercase text-[#d4af37]">True labour cost</p>
          <p className="font-display text-3xl mt-2">£{d.cost_per_week}<span className="text-sm text-[#a3a3a3]"> / week</span></p>
          <p className="text-[#d4af37] text-sm mt-1">£{d.cost_per_visit}/visit · £{d.rate_per_hour}/hr · 2×5h (Mon & Thu)</p>
          <p className="text-[#a3a3a3] text-xs mt-2">£0 markup · £0 admin · £0 booking fee. Auto-updates with cleaner wage.</p>
        </div>
        <div className="bg-white border border-[#e8e5de] rounded-sm p-5">
          <p className="text-[11px] uppercase tracking-luxe text-[#b5952f] mb-2">Arrangement</p>
          <p className="text-sm">Status: <b className={cfg.active && !cfg.paused ? "text-green-600" : "text-[#b5952f]"}>{!cfg.active ? "Stopped" : cfg.paused ? "Paused" : "Active"}</b></p>
          <p className="text-sm mt-1">Mon & Thu · 12:00–17:00 · 5h each</p>
          <p className="text-sm mt-1 text-[#777]">Cleaner: {cleaner?.name || "Not set"} · {cfg.postcode || "No postcode"}</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={gen} data-testid="owner-generate" className="btn-gold px-3 py-1.5 rounded-sm text-xs">Generate next visits</button>
            {cfg.paused ? <button onClick={() => pause(false)} data-testid="owner-resume" className="btn-outline-gold px-3 py-1.5 rounded-sm text-xs">Resume</button>
                        : <button onClick={() => pause(true)} data-testid="owner-pause" className="btn-outline-gold px-3 py-1.5 rounded-sm text-xs">Pause</button>}
            <button onClick={cancelRec} className="px-3 py-1.5 rounded-sm text-xs border border-red-300 text-red-500">Stop recurring</button>
          </div>
        </div>
        <div className="bg-white border border-[#e8e5de] rounded-sm p-5">
          <div className="flex justify-between items-center mb-2"><p className="text-[11px] uppercase tracking-luxe text-[#b5952f]">My priorities</p>
            <button onClick={addPriority} data-testid="owner-add-priority" className="text-xs text-[#b5952f]">+ Add</button></div>
          {d.priorities.length === 0 ? <p className="text-[#bbb] text-sm">None set.</p> : d.priorities.map((p) => (
            <div key={p.id} className="flex justify-between text-sm py-1 border-b border-[#f3f1ea]">
              <span><span className={`text-[10px] mr-1 ${p.level === "URGENT" ? "text-red-500" : p.level === "HIGH" ? "text-[#b5952f]" : "text-[#999]"}`}>{p.level}</span>{p.text}</span>
              <button onClick={() => delPriority(p.id)} className="text-xs text-[#bbb]">✕</button>
            </div>
          ))}
        </div>
      </div>
      {report && (
        <div className="bg-white border border-[#e8e5de] rounded-sm p-5 mb-6" data-testid="owner-cost-report">
          <p className="text-[11px] uppercase tracking-luxe text-[#b5952f] mb-1">Cost report — what my benefit costs the company</p>
          <p className="text-sm text-[#555] mb-3">Service revenue £0 · markup £0 · admin £0 · booking fee £0. Totals: {report.total_visits} visits · {report.total_hours}h · <b>£{report.total_cost}</b> labour.</p>
          <table className="w-full text-sm"><thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#eee]">{["Month", "Visits", "Hours", "Labour cost"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{report.months.map((m) => <tr key={m.month} className="border-b border-[#f3f1ea]"><td className="px-3 py-2">{m.month}</td><td className="px-3 py-2">{m.visits}</td><td className="px-3 py-2">{m.hours}</td><td className="px-3 py-2">£{m.labour_cost}</td></tr>)}
            {report.months.length === 0 && <tr><td colSpan="4" className="px-3 py-5 text-center text-[#8a8a8a]">No owner visits yet.</td></tr>}</tbody></table>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#e8e5de] rounded-sm p-4">
          <p className="text-[11px] uppercase tracking-luxe text-[#b5952f] mb-2">Upcoming visits · {d.upcoming_visits.length}</p>
          {d.upcoming_visits.length === 0 ? <p className="text-[#bbb] text-sm">None — click "Generate next visits".</p> : d.upcoming_visits.map((b) => (
            <div key={b.id} className="flex justify-between text-sm border-b border-[#f3f1ea] py-2">
              <span><b>{b.reference}</b> · {b.date} · {b.arrival_window} <span className="text-[#999]">{b.assigned_staff?.name || "Unassigned"}</span></span>
              <span>£{b.owner_cost} <button onClick={() => cancelVisit(b.id)} className="text-xs text-red-500 ml-2">Cancel</button></span>
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#e8e5de] rounded-sm p-4">
          <p className="text-[11px] uppercase tracking-luxe text-[#b5952f] mb-2">Rotating Elite tasks</p>
          {d.rotating_tasks.map((t) => (
            <div key={t.id} className="flex justify-between text-sm border-b border-[#f3f1ea] py-2">
              <span>{t.label}</span>
              <span className="text-[#999]">{t.last_completed ? `Done ${t.last_completed}` : "Never"} · next {t.next_recommended || "due"}</span>
            </div>
          ))}
        </div>
      </div>
      {edit && (
        <Modal onClose={() => setEdit(null)} title="Owner Elite Signature — settings">
          <div className="space-y-3 mt-3">
            <div><label className="text-xs uppercase text-[#555] block mb-1">Home address</label><input className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" data-testid="owner-address" value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} /></div>
            <div><label className="text-xs uppercase text-[#555] block mb-1">Postcode (for travel routing)</label><input className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" data-testid="owner-postcode" value={edit.postcode} onChange={(e) => setEdit({ ...edit, postcode: e.target.value })} /></div>
            <div><label className="text-xs uppercase text-[#555] block mb-1">Access info (keys/alarm/parking)</label><input className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" value={edit.access_info} onChange={(e) => setEdit({ ...edit, access_info: e.target.value })} /></div>
            <div><label className="text-xs uppercase text-[#555] block mb-1">Preferred cleaner (auto-assigned if free)</label>
              <select className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" data-testid="owner-preferred-cleaner" value={edit.preferred_cleaner_id} onChange={(e) => setEdit({ ...edit, preferred_cleaner_id: e.target.value })}>
                <option value="">— None —</option>
                {staff.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <button onClick={saveSettings} data-testid="owner-save-settings" className="btn-gold w-full py-2.5 rounded-sm text-sm">Save settings</button>
          </div>
        </Modal>
      )}
    </div>
  );
}



// ---------------- Specialist Pricing (Admin) ----------------
export function SpecialistPricing({ isManagement }) {
  const [rows, setRows] = useState([]);
  const [std, setStd] = useState(null);
  const [equip, setEquip] = useState([]);
  const load = () => {
    api.get("/specialist/admin").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/specialist/services").then(({ data }) => setStd(data.standard_pricing)).catch(() => {});
    api.get("/equipment").then(({ data }) => setEquip(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const savePrice = async (key, price) => { try { await api.put(`/specialist/services/${key}`, { price: parseFloat(price) }); toast.success("Price updated"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggle = async (key, active) => { try { await api.put(`/specialist/services/${key}`, { active }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const saveStd = async (k, v) => { try { const { data } = await api.put("/specialist/standard-pricing", { [k]: parseFloat(v) }); setStd(data); toast.success("Standard pricing updated"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggleEquip = async (key, available) => { try { await api.put(`/equipment/${key}`, { available }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const byCat = {};
  rows.forEach((r) => { (byCat[r.category] = byCat[r.category] || []).push(r); });
  return (
    <div data-testid="specialist-pricing">
      <h2 className="font-display text-3xl tracking-tight mb-1">Specialist Pricing</h2>
      <p className="text-[#8a8a8a] text-sm mb-5">Change prices anytime — updates apply to new bookings only, never completed/paid ones.</p>
      {std && (
        <div className="bg-white border border-[#e8e5de] rounded-sm p-5 mb-6">
          <p className="text-[11px] uppercase tracking-luxe text-[#b5952f] mb-3">Standard hourly (min {std.min_hours}h)</p>
          <div className="grid grid-cols-3 gap-3">
            {["Essential", "Premium", "Elite"].map((k) => (
              <div key={k}><label className="text-xs text-[#555] block mb-1">{k} (£/hr)</label>
                <input type="number" defaultValue={std[k]} disabled={!isManagement} onBlur={(e) => saveStd(k, e.target.value)} data-testid={`std-${k}`} className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" /></div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white border border-[#e8e5de] rounded-sm p-5 mb-6">
        <p className="text-[11px] uppercase tracking-luxe text-[#b5952f] mb-3">Equipment availability (gates specialist allocation)</p>
        <div className="flex flex-wrap gap-2">
          {equip.map((e) => (
            <button key={e.key} onClick={() => toggleEquip(e.key, !e.available)} data-testid={`equip-${e.key}`}
              className={`px-3 py-1.5 rounded-sm text-xs border ${e.available ? "border-green-300 bg-green-50 text-green-700" : "border-red-300 bg-red-50 text-red-600"}`}>
              {e.name} · {e.available ? "Available" : "Unavailable"}
            </button>
          ))}
        </div>
      </div>
      {Object.entries(byCat).map(([cat, items]) => (
        <div key={cat} className="bg-white border border-[#e8e5de] rounded-sm mb-4 overflow-hidden">
          <p className="px-4 py-3 text-[11px] uppercase tracking-luxe text-[#b5952f] border-b border-[#eee]">{cat}</p>
          <table className="w-full text-sm"><tbody>
            {items.map((it) => (
              <tr key={it.key} className="border-b border-[#f3f1ea]" data-testid={`spec-price-row-${it.key}`}>
                <td className="px-4 py-2">{it.name}{it.requires_quote && <span className="text-[10px] text-[#b5952f] ml-2 uppercase">quote</span>}</td>
                <td className="px-4 py-2 w-40"><div className="flex items-center gap-1"><span className="text-[#8a8a8a]">£</span>
                  <input type="number" defaultValue={it.price} disabled={!isManagement} onBlur={(e) => savePrice(it.key, e.target.value)} data-testid={`spec-price-${it.key}`} className="w-24 border border-[#e0ddd4] rounded-sm px-2 py-1 text-sm" />
                  <span className="text-xs text-[#999]">{it.unit === "per_hour" ? "/hr" : it.unit === "quote" ? "from" : ""}</span></div></td>
                <td className="px-4 py-2 text-right">{isManagement && <button onClick={() => toggle(it.key, !it.active)} className={`text-xs ${it.active ? "text-[#b5952f]" : "text-green-600"}`}>{it.active ? "Disable" : "Enable"}</button>}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      ))}
    </div>
  );
}
